import { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Avatar, Box, Button, Flex, HStack, Switch, Tag, Text, Tooltip, useToast, VStack } from '@bonusly/design-system';

import { useBizyDreamingPreferences, useUpdateBizyDreamingPreferenceMutation } from '../../queries/bizy-dreaming-preferences';
import { useGithubAuthorize, useGithubDisconnect, useGithubStatus } from '../../queries/github';
import { BizySettingsSecondaryView } from '../bizy-settings/bizy-settings-secondary-view';
import { GithubLogo } from './github-logo';
import { GithubScopePicker } from './github-scope-picker';

interface GithubManagementViewProps {
	onBack: () => void;
}

export const GithubManagementView: FC<GithubManagementViewProps> = ({ onBack }) => {
	const { t } = useTranslation('bizy_settings', { keyPrefix: 'github' });
	const { data: status, isLoading: isLoadingStatus } = useGithubStatus();
	const { mutateAsync: disconnect, isLoading: isDisconnecting } = useGithubDisconnect();
	const { mutateAsync: authorize, isLoading: isAuthorizing } = useGithubAuthorize();
	const { data: dreamingPreferences } = useBizyDreamingPreferences();
	const { mutate: updateDreaming, isLoading: isUpdatingDreaming } = useUpdateBizyDreamingPreferenceMutation();
	const { track } = useAnalytics();
	const toast = useToast();

	const selectedOrgs = status?.selectedOrgs ?? [];
	const selectedRepos = status?.selectedRepos ?? [];
	const isConnected = status?.status === 'connected';
	const isExpired = status?.status === 'expired';

	const githubDreaming = dreamingPreferences?.dreamingPreferences.github;
	// Only surface the per-user dreaming opt-out when Bizy actually dreams about
	// this member (personally connected OR org-configured + mapped).
	const isDreamingApplicable = !!githubDreaming?.applicable;
	// Display snapshot of the connected GitHub account so the member can confirm
	// the right identity is attached. May be absent (or have null fields) for
	// connections made before the snapshot shipped.
	const identity = githubDreaming?.identity ?? null;
	const identityName = identity?.name ?? identity?.login ?? null;
	// The member linked their own GitHub token (they own the connection and can
	// disconnect it) vs. being attached solely through the admin's org mapping
	// (read-only from their perspective — only the admin can change it).
	const hasPersonalToken = !!githubDreaming?.personallyConnected;
	const isCompanyConnected = !!githubDreaming?.companyConfigured;
	const isOrgMapped = !hasPersonalToken && !!identity;
	const showProfileBadge = hasPersonalToken || !!identity;
	// Company connector backs dreaming but the member hasn't linked their own
	// token — invite them to (optionally) connect for richer, personal data.
	const canConnectPersonal = !hasPersonalToken && isCompanyConnected;
	// Workspace syncing is on, but this member has no GitHub identity at all yet
	// (no personal token and no org mapping). Bizy isn't dreaming about them — so
	// this is an onboarding nudge, not an opt-out, and gets a fuller persona guide.
	const isUnmappedCompanyUser = isCompanyConnected && !hasPersonalToken && !isOrgMapped;

	// On return from the OAuth redirect the status query resolves to "connected";
	// mark the personal connect as complete and clear the marker we set before
	// leaving.
	useEffect(() => {
		if (isConnected && sessionStorage.getItem('bizy_connecting_github')) {
			track(AnalyticsEvent.Github.Connected);
			sessionStorage.removeItem('bizy_connecting_github');
		}
	}, [isConnected, track]);

	const handleDisconnect = useCallback(async () => {
		try {
			await disconnect();
			track(AnalyticsEvent.Github.Disconnected);
			onBack();
		} catch {
			// Error handled by React Query
		}
	}, [disconnect, onBack, track]);

	const handleConnect = useCallback(async () => {
		track(AnalyticsEvent.Github.ConnectClicked);
		try {
			const { redirectUrl } = await authorize(window.location.pathname);
			if (redirectUrl) {
				sessionStorage.setItem('bizy_connecting_github', 'true');
				window.location.href = redirectUrl;
			}
		} catch {
			// Error handled by React Query
		}
	}, [authorize, track]);

	const handleToggleDreaming = useCallback(
		(enabled: boolean) => {
			track(AnalyticsEvent.Github.DreamingToggled);
			updateDreaming(
				{ connector: 'github', enabled },
				{
					onError: () => {
						toast({ title: t('dreaming.updateError'), status: 'error', duration: 3000 });
					},
				}
			);
		},
		[t, toast, track, updateDreaming]
	);

	if (isLoadingStatus) return null;

	// Subtle rule that separates each stacked section once we've broken out of the
	// single-card wrapper. Applied to every section after the header.
	const sectionDivider = { borderTop: '1px solid', borderColor: 'border.soft', pt: 6 } as const;

	return (
		<BizySettingsSecondaryView>
			<Box maxW="2xl" mx="auto" w="full">
				<VStack align="stretch" spacing={6}>
					<HStack align="flex-start" spacing={4}>
						<Box color="text.default" flexShrink={0} display="inline-flex" alignItems="center" mt={1}>
							<GithubLogo width="32" height="32" />
						</Box>
						<Box>
							<Text size="lg" variant="strong" color="text.default" mb={1}>
								{t('management.title')}
							</Text>
							<Text color="text.softer" size="sm">
								{t('management.description')}
							</Text>
						</Box>
					</HStack>

					{showProfileBadge && (
						// Compact bordered identity card, mirroring the admin teammate-mapping rows.
						<Flex align="center" gap={3} borderWidth="1px" borderColor="border.default" borderRadius="lg" bg="bg.surface" px={3} py={2}>
							<Avatar size="sm" name={identityName ?? 'GitHub'} src={identity?.avatarUrl ?? undefined} />
							<VStack spacing={0} align="flex-start" flex={1} minW={0}>
								<Text fontSize="sm" fontWeight="medium" color="text.default" noOfLines={1}>
									{identityName ?? t('connectedAs.fallback')}
								</Text>
								{identity?.login && (
									<Text fontSize="xs" color="text.softer" noOfLines={1}>
										@{identity.login}
									</Text>
								)}
							</VStack>
							{hasPersonalToken ? (
								<Button colorScheme="danger" isLoading={isDisconnecting} onClick={handleDisconnect} size="sm" variant="ghost" flexShrink={0}>
									{t('management.disconnect')}
								</Button>
							) : (
								isOrgMapped && (
									<Tooltip label={t('workspaceMapped.tooltip')} hasArrow placement="top" openDelay={200} maxW="260px">
										<Box as="span" display="inline-flex" flexShrink={0}>
											<Tag size="sm" colorScheme="gray" variant="subtle">
												{t('workspaceMapped.tag')}
											</Tag>
										</Box>
									</Tooltip>
								)
							)}
						</Flex>
					)}

					{isDreamingApplicable && (
						<Box {...sectionDivider}>
							<Flex align="center" justify="space-between" gap={4}>
								<Text size="md" variant="strong" color="text.default">
									{t('dreaming.label')}
								</Text>
								<Switch
									aria-label={t('dreaming.toggleAriaLabel')}
									flexShrink={0}
									isChecked={!!githubDreaming?.enabled}
									isDisabled={isUpdatingDreaming}
									onChange={(event) => handleToggleDreaming(event.target.checked)}
								/>
							</Flex>
							<Text size="sm" color="text.softer" mt={2}>
								{t('dreaming.description')}
							</Text>
						</Box>
					)}

					{isConnected ? (
						<Box {...sectionDivider}>
							<GithubScopePicker selectedOrgs={selectedOrgs} selectedRepos={selectedRepos} />
						</Box>
					) : (
						canConnectPersonal && (
							<Box {...sectionDivider}>
								<VStack align="flex-start" spacing={3}>
									<VStack align="flex-start" spacing={2.5}>
										<Text size="md" variant="strong" color="text.default">
											{t('connectPersonal.title')}
										</Text>
										<Text size="sm" color="text.softer">
											{isUnmappedCompanyUser ? t('connectPersonal.unmapped') : t('connectPersonal.description')}
										</Text>
									</VStack>
									<Button size="md" colorScheme="primary" onClick={handleConnect} isLoading={isAuthorizing}>
										{isExpired ? t('reconnect') : t('connectPersonal.action')}
									</Button>
								</VStack>
							</Box>
						)
					)}
				</VStack>
			</Box>
		</BizySettingsSecondaryView>
	);
};
